import type { Dispatch as ReduxDispatch } from '@reduxjs/toolkit';

import type {
  Insurance,
  InsuranceBrandingResponse,
  InsuranceProviderResponse,
  InsuranceResponse,
} from '../@types/Insurance';
import type { BaseAction } from '../@types/actions';
import * as http from '../Http';
import { buildGetRequest, buildPostRequest } from '../Http/requestTemplates';
import { get as getSettings } from '../helpers/settings';
import requestCache from '../utils/request-cache';

export const REQ_FETCH_INSURANCE = 'REQ/FETCH_INSURANCE';
export const RCV_FETCH_INSURANCE = 'RCV/FETCH_INSURANCE';
type REQ_FETCH_INSURANCE_TYPE = BaseAction<typeof REQ_FETCH_INSURANCE>;
type RCV_FETCH_INSURANCE_TYPE = BaseAction<typeof RCV_FETCH_INSURANCE> & {
  response: Insurance[];
};

export const REQ_INSURANCE_PROVIDER = 'REQ/INSURANCE_PROVIDER';
export const RCV_INSURANCE_PROVIDER = 'RCV/INSURANCE_PROVIDER';
type REQ_INSURANCE_PROVIDER_TYPE = BaseAction<typeof REQ_INSURANCE_PROVIDER>;
type RCV_INSURANCE_PROVIDER_TYPE = BaseAction<typeof RCV_INSURANCE_PROVIDER> & {
  response: InsuranceProviderResponse;
};

export const REQ_INSURANCE_INQUIRY = 'REQ/INSURANCE_INQUIRY';
export const RCV_INSURANCE_INQUIRY = 'RCV/INSURANCE_INQUIRY';
type REQ_INSURANCE_INQUIRY_TYPE = BaseAction<typeof REQ_INSURANCE_INQUIRY>;
type RCV_INSURANCE_INQUIRY = BaseAction<typeof RCV_INSURANCE_INQUIRY> & {
  response: Insurance[];
};

export const REQ_INSURANCE_BRANDING = 'REQ/INSURANCE_BRANDING';
export const RCV_INSURANCE_BRANDING = 'RCV/INSURANCE_BRANDING';
type REQ_INSURANCE_BRANDING_TYPE = BaseAction<typeof REQ_INSURANCE_BRANDING>;
type RCV_INSURANCE_BRANDING_TYPE = BaseAction<typeof RCV_INSURANCE_BRANDING> & {
  response: InsuranceBrandingResponse;
};

export const REQ_PERSIST_DATA = 'REQ/PERSIST_DATA';
type REQ_PERSIST_DATA_TYPE = BaseAction<typeof REQ_PERSIST_DATA> & {
  personalNumber: string;
  drivingRange: string;
};

export const REQ_CLEAR_PERSIST_DATA = 'REQ/CLEAR_PERSIST_DATA';
type REQ_CLEAR_PERSIST_DATA_TYPE = BaseAction<typeof REQ_CLEAR_PERSIST_DATA>;

export const REQ_CLEAR_INSURANCE = 'REQ/CLEAR_INSURANCE';
type REQ_CLEAR_INSURANCE_TYPE = BaseAction<typeof REQ_CLEAR_INSURANCE>;

export const REQUEST_FAILED = 'INSURANCE/REQUEST_FAILED';
type REQUEST_FAILED_TYPE = BaseAction<typeof REQUEST_FAILED> & {
  action:
    | typeof REQ_FETCH_INSURANCE
    | typeof REQ_INSURANCE_BRANDING
    | typeof REQ_INSURANCE_INQUIRY
    | typeof REQ_INSURANCE_PROVIDER;
};

export type INSURANCE_ACTION =
  | REQ_INSURANCE_INQUIRY_TYPE
  | RCV_INSURANCE_INQUIRY
  | REQ_FETCH_INSURANCE_TYPE
  | RCV_FETCH_INSURANCE_TYPE
  | REQ_INSURANCE_PROVIDER_TYPE
  | RCV_INSURANCE_PROVIDER_TYPE
  | REQ_INSURANCE_BRANDING_TYPE
  | RCV_INSURANCE_BRANDING_TYPE
  | REQ_PERSIST_DATA_TYPE
  | REQ_CLEAR_PERSIST_DATA_TYPE
  | REQ_CLEAR_INSURANCE_TYPE
  | REQUEST_FAILED_TYPE;

type Dispatch = ReduxDispatch<INSURANCE_ACTION>;

interface InsuranceOption {
  externalId: string;
  dealerType: string;
}

interface Vehicle {
  registrationNumber: string;
  manufacturer: string;
}

export const get =
  (insuranceOption: InsuranceOption, vehicle: Vehicle, personalNumber: string, range: string) =>
  async (dispatch: Dispatch) => {
    const payload = {
      CarRegistrationNumber: vehicle.registrationNumber,
      SocialSecurityNumber: personalNumber,
      DrivingDistanceClass: range,
      CarBrand: vehicle.manufacturer.toUpperCase(),
      DealerId: insuranceOption.externalId,
      DealerType: insuranceOption.dealerType,
      ReturnInsuranceItems: true,
      ReturnInsuranceDescription: true,
      AddOns: [],
    };

    try {
      dispatch({ type: REQ_FETCH_INSURANCE });

      const response = await http.httpJson<InsuranceResponse>(
        getSettings('IfInsuranceUri') as string,
        buildPostRequest(payload),
      );
      dispatch({
        type: RCV_FETCH_INSURANCE,
        response: response.insurances,
      });
    } catch (_e) {
      dispatch({ type: REQUEST_FAILED, action: REQ_FETCH_INSURANCE });
    }
  };

export const inquiry =
  (vehicleId: string, branchId: string, personalNumber: string, range: string) =>
  async (dispatch: Dispatch) => {
    const payload = {
      vehicleId,
      branchId,
      socialId: personalNumber,
      drivingDistance: range,
    };

    try {
      if (requestCache.isPending(`${REQ_INSURANCE_INQUIRY}/${vehicleId}/${branchId}`)) {
        return;
      }

      requestCache.isPending(`${REQ_INSURANCE_INQUIRY}/${vehicleId}/${branchId}`, true);

      dispatch({ type: REQ_INSURANCE_INQUIRY });

      const response = await http.json<InsuranceResponse>(
        '/api/insurance/inquiry',
        buildPostRequest(payload),
      );

      dispatch({
        type: RCV_INSURANCE_INQUIRY,
        response: response.response.insurances,
      });
    } catch (_e) {
      dispatch({ type: REQUEST_FAILED, action: REQ_INSURANCE_INQUIRY });
    }
    requestCache.isPending(`${REQ_INSURANCE_INQUIRY}/${vehicleId}/${branchId}`, false);
  };

export const branding =
  (insuranceOption: InsuranceOption, vehicle: Vehicle) => async (dispatch: Dispatch) => {
    const payload = {
      CarRegistrationNumber: vehicle.registrationNumber,
      CarBrand: vehicle.manufacturer.toUpperCase(),
      DealerId: insuranceOption.externalId,
      DealerType: insuranceOption.dealerType,
    };

    try {
      dispatch({ type: REQ_INSURANCE_BRANDING });

      const response = await http.httpJson<InsuranceBrandingResponse>(
        getSettings('IfBrandingUri') as string,
        buildPostRequest(payload),
      );
      dispatch({
        type: RCV_INSURANCE_BRANDING,
        response,
      });
    } catch (_e) {
      dispatch({ type: REQUEST_FAILED, action: REQ_INSURANCE_BRANDING });
    }
  };

export const getInsuranceProvider =
  (vehicleId: string, branchId: string) => async (dispatch: Dispatch) => {
    try {
      if (requestCache.isPending(`${REQ_INSURANCE_PROVIDER}/${vehicleId}/${branchId}`)) {
        return;
      }

      requestCache.isPending(`${REQ_INSURANCE_PROVIDER}/${vehicleId}/${branchId}`, true);

      dispatch({ type: REQ_INSURANCE_PROVIDER });

      const response = await http.json<InsuranceProviderResponse>(
        `/api/insurance/provider/${vehicleId}?branchId=${branchId}`,
        buildGetRequest(),
      );
      http.validateResponse(response);
      dispatch({
        type: RCV_INSURANCE_PROVIDER,
        response: response.response,
      });
    } catch (_e) {
      dispatch({ type: REQUEST_FAILED, action: REQ_INSURANCE_PROVIDER });
    }
    requestCache.isPending(`${REQ_INSURANCE_PROVIDER}/${vehicleId}/${branchId}`, false);
  };

export const save = (personalNumber: string, drivingRange: string) => (dispatch: Dispatch) =>
  dispatch({ type: REQ_PERSIST_DATA, personalNumber, drivingRange });

export const removePersistedData = () => (dispatch: Dispatch) =>
  dispatch({ type: REQ_CLEAR_PERSIST_DATA });

export const clear = () => (dispatch: Dispatch) => dispatch({ type: REQ_CLEAR_INSURANCE });
